/* eslint-disable no-console */
import React, { useEffect, useMemo, useState } from 'react';

import * as Sentry from '@sentry/react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Copy, Link2, Mail, Plus, Share2, X } from 'lucide-react';
import { Button } from 'shareBrightSpark/ui/button';
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogHeader,
   DialogTitle,
} from 'shareBrightSpark/ui/dialog';
import { Input } from 'shareBrightSpark/ui/input';
import { Switch } from 'shareBrightSpark/ui/switch';

const MAX_SHARE_EMAIL_FIELDS = 10;
/** How long to show "Copied" after a successful copy (copy itself is instant). */
const COPY_FEEDBACK_MS = 800;

interface ShareDialogProps {
   open: boolean;
   onClose: () => void;
   onShare?: (emails: string[]) => Promise<void> | void;
   shareUrl?: string;
   /** When false, shared link locks the Tools panel for recipients. */
   recipientCanEdit?: boolean;
   onRecipientCanEditChange?: (value: boolean) => void;
   response?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
   };
}

export const ShareDialog = ({
   open,
   onClose,
   onShare,
   shareUrl,
   recipientCanEdit = false,
   onRecipientCanEditChange,
   response,
}: ShareDialogProps) => {
   const [emails, setEmails] = useState<string[]>(['']);
   const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
   const [isLoading, setIsLoading] = useState(false);

   useEffect(() => {
      if (open) {
         setEmails(['']);
         setCopyStatus('idle');
         setIsLoading(false);
         Sentry.captureMessage('User Shared Preview - Dialog Opened', {
            level: 'info',
            extra: { shareUrl: shareUrl ?? '' },
         });
      }
   }, [open, shareUrl]);

   const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);

   const handleChangeEmail = (index: number, value: string) => {
      setEmails((prev) => prev.map((email, idx) => (idx === index ? value : email)));
   };

   const handleAddEmail = () => {
      setEmails((prev) => (prev.length >= MAX_SHARE_EMAIL_FIELDS ? prev : [...prev, '']));
   };

   const sendEmailsToApi = async (emailsToSend: string[], url: string) => {
      const res = await fetch(
         `${process.env.REACT_APP_SCODIX_API_URL}/visualization/prospects/send-email`,
         {
            method: 'POST',
            headers: {
               Authorization: `Bearer my_secure_token_123`,
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               emails: emailsToSend,
               share_url: url,
            }),
         },
      );

      if (!res.ok) {
         const errorData = await res.json();
         throw new Error(errorData.message || `Failed to send emails: ${res.status}`);
      }

      return await res.json();
   };

   const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const formattedEmails = emails
         .map((email) => email.trim())
         .filter((email) => email.length > 0 && emailRegex.test(email));

      if (formattedEmails.length === 0) return;

      if (response && shareUrl) {
         try {
            setIsLoading(true);

            const result = await sendEmailsToApi(formattedEmails, shareUrl);

            if (result.status === 'success') {
               console.log(`Successfully sent ${result.sent_count} email(s)`);
               Sentry.captureMessage('User Shared Preview - Send Email', {
                  level: 'info',
                  extra: { shareUrl, emails: formattedEmails, sentCount: result.sent_count },
               });

               if (result.failed_emails && result.failed_emails.length > 0) {
                  console.warn('Failed emails:', result.failed_emails);
               }

               response.onSuccess?.();
            } else {
               throw new Error(result.message || 'Failed to send emails');
            }

            onClose();
         } catch (error) {
            console.error('Failed to send emails:', error);
            response.onError?.(error as Error);
         } finally {
            setIsLoading(false);
         }
      } else if (onShare) {
         await onShare(formattedEmails);
         if (shareUrl) {
            Sentry.captureMessage('User Shared Preview - Send Email', {
               level: 'info',
               extra: { shareUrl, emails: formattedEmails },
            });
         }
         onClose();
      }
   };

   const isShareDisabled = useMemo(() => {
      const trimmed = emails.map((email) => email.trim());
      if (trimmed.every((email) => email === '')) return true;
      return trimmed.some((email) => email !== '' && !emailRegex.test(email));
   }, [emails, emailRegex]);

   const handleCopyLink = async () => {
      if (!shareUrl) return;
      try {
         if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(shareUrl);
            Sentry.captureMessage('User Shared Preview - Copy Link', {
               level: 'info',
               extra: { shareUrl },
            });
            setCopyStatus('copied');
            setTimeout(() => setCopyStatus('idle'), COPY_FEEDBACK_MS);
         } else {
            const textarea = document.createElement('textarea');
            textarea.value = shareUrl;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            Sentry.captureMessage('User Shared Preview - Copy Link', {
               level: 'info',
               extra: { shareUrl },
            });
            setCopyStatus('copied');
            setTimeout(() => setCopyStatus('idle'), COPY_FEEDBACK_MS);
         }
      } catch (e) {
         setCopyStatus('error');
         setTimeout(() => setCopyStatus('idle'), COPY_FEEDBACK_MS);
      }
   };

   return (
      <Dialog
         open={open}
         onOpenChange={(next) => {
            if (!next) onClose();
         }}
      >
         <DialogContent className='max-w-[488px] border-none bg-card p-0 shadow-2xl shadow-share-gold/10 overflow-hidden'>
            <div className='relative px-7 pt-7 pb-1'>
               <div className='absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-share-gold via-share-gold-glow to-share-gold opacity-80 rounded-t-lg' />
               <DialogHeader className='space-y-1.5'>
                  <div className='flex items-center gap-2.5'>
                     <div className='flex h-9 w-9 items-center justify-center rounded-xl bg-share-gold-light'>
                        <Share2 className='h-4 w-4 text-share-gold' />
                     </div>
                     <DialogTitle className='text-xl font-bold tracking-tight text-foreground'>
                        Share RealView
                     </DialogTitle>
                  </div>
                  <DialogDescription className='text-sm text-muted-foreground pl-[46px]'>
                     Give your prospect a live preview of enhancements via a link
                  </DialogDescription>
               </DialogHeader>
            </div>

            <form onSubmit={handleSubmit} noValidate className='px-7 pb-7 space-y-5'>
               {onRecipientCanEditChange ? (
                  <div className='flex items-center gap-3 rounded-xl bg-share-gold-light/50 p-3.5 transition-colors'>
                     <Switch
                        checked={recipientCanEdit}
                        onCheckedChange={onRecipientCanEditChange}
                        disabled={isLoading}
                        className='data-[state=checked]:bg-share-gold data-[state=unchecked]:bg-border'
                     />
                     <span className='text-sm font-medium text-foreground leading-snug'>
                        Allow recipients to use tools
                     </span>
                  </div>
               ) : null}

               {shareUrl ? (
                  <div className='space-y-2'>
                     <label className='flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                        <Link2 className='h-3.5 w-3.5' />
                        Encrypted link
                     </label>
                     <div className='flex gap-2'>
                        <div className='relative flex-1 min-w-0'>
                           <Input
                              value={shareUrl}
                              readOnly
                              onClick={() => void handleCopyLink()}
                              className='h-11 bg-share-surface border-border pr-3 text-sm text-muted-foreground font-mono truncate cursor-pointer focus-visible:ring-share-gold/30'
                              aria-label='Share link'
                           />
                        </div>
                        <Button
                           type='button'
                           onClick={() => void handleCopyLink()}
                           variant='outline'
                           className='h-11 min-w-[110px] shrink-0 border-share-gold/30 text-share-gold font-semibold tracking-wide hover:bg-share-gold-light hover:text-share-gold transition-all'
                        >
                           <AnimatePresence mode='wait'>
                              {copyStatus === 'copied' ? (
                                 <motion.span
                                    key='check'
                                    initial={{ scale: 0.5, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.5, opacity: 0 }}
                                    className='flex items-center gap-1.5'
                                 >
                                    <Check className='h-4 w-4' />
                                    Copied
                                 </motion.span>
                              ) : (
                                 <motion.span
                                    key='copy'
                                    initial={{ scale: 0.5, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.5, opacity: 0 }}
                                    className='flex items-center gap-1.5'
                                 >
                                    <Copy className='h-4 w-4' />
                                    {copyStatus === 'error' ? 'Error' : 'Copy link'}
                                 </motion.span>
                              )}
                           </AnimatePresence>
                        </Button>
                     </div>
                  </div>
               ) : null}

               <div className='space-y-2.5'>
                  <label className='flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                     <Mail className='h-3.5 w-3.5' />
                     Email addresses
                  </label>
                  <div className='space-y-2'>
                     <AnimatePresence>
                        {emails.map((email, index) => (
                           <motion.div
                              key={index}
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className='flex gap-2'
                           >
                              <Input
                                 type='email'
                                 placeholder='prospect@example.com'
                                 value={email}
                                 onChange={(e) => handleChangeEmail(index, e.target.value)}
                                 className='h-11 bg-share-surface border-border text-sm focus-visible:ring-share-gold/30'
                                 autoFocus={index === 0}
                                 disabled={isLoading}
                              />
                              {emails.length > 1 ? (
                                 <button
                                    type='button'
                                    onClick={() =>
                                       setEmails((prev) => prev.filter((_, i) => i !== index))
                                    }
                                    className='flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:text-share-danger hover:bg-destructive/10 transition-colors'
                                    aria-label='Remove email'
                                 >
                                    <X className='h-4 w-4' />
                                 </button>
                              ) : null}
                           </motion.div>
                        ))}
                     </AnimatePresence>
                  </div>
                  {emails.length < MAX_SHARE_EMAIL_FIELDS ? (
                     <button
                        type='button'
                        onClick={handleAddEmail}
                        className='flex items-center gap-1.5 text-sm font-medium text-share-gold hover:text-share-gold-glow transition-colors group'
                     >
                        <span className='flex h-6 w-6 items-center justify-center rounded-full border border-share-gold/30 group-hover:border-share-gold/50 group-hover:bg-share-gold-light transition-all'>
                           <Plus className='h-3 w-3' />
                        </span>
                        Add another
                     </button>
                  ) : null}
               </div>

               <div className='flex justify-end gap-3 pt-2 border-t border-border'>
                  <Button
                     type='button'
                     variant='ghost'
                     onClick={onClose}
                     disabled={isLoading}
                     className='text-share-danger font-semibold tracking-wide hover:bg-destructive/10 hover:text-share-danger'
                  >
                     Cancel
                  </Button>
                  <Button
                     type='submit'
                     disabled={isShareDisabled || isLoading}
                     className='bg-share-gold hover:bg-share-gold/90 text-primary-foreground font-semibold tracking-wide shadow-lg shadow-share-gold/20 px-7'
                  >
                     {isLoading ? 'Sending...' : 'Share'}
                  </Button>
               </div>
            </form>
         </DialogContent>
      </Dialog>
   );
};

export default ShareDialog;
